import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Ambulance, Hospital, Zap, ShieldCheck, MapPin, PhoneCall, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation as useGeo } from "@/contexts/LocationContext";
import LocationBar from "@/components/LocationBar";

const AMB_TYPES = [
  { key: "basic", label: "Basic Life Support", desc: "Oxygen, first aid", price: 800, icon: Ambulance },
  { key: "als", label: "Advanced Life Support (ALS)", desc: "Paramedic, defibrillator", price: 2500, icon: Zap },
  { key: "icu", label: "ICU Ambulance", desc: "Ventilator, ICU nurse", price: 4500, icon: ShieldCheck },
];

export default function EmergencyPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { loc, request: requestLoc } = useGeo();
  const [tab, setTab] = useState("bed");
  const [hospitals, setHospitals] = useState([]);
  const [bedH, setBedH] = useState(null);
  const [bedType, setBedType] = useState("general");
  const [pName, setPName] = useState("");
  const [contact, setContact] = useState("");
  const [ambOpen, setAmbOpen] = useState(false);
  const [ambType, setAmbType] = useState("basic");
  const [pickup, setPickup] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [dropMode, setDropMode] = useState("hospital"); // "hospital" | "custom"
  const [destH, setDestH] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    const p = loc ? { near_lat: loc.lat, near_lng: loc.lng } : {};
    return api.get("/hospitals", { params: p }).then((r) => setHospitals(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loc?.lat, loc?.lng]);

  const bookBed = async () => {
    if (!user) { nav("/login"); return; }
    if (!pName || !contact) { toast.error("Fill patient details"); return; }
    setLoading(true);
    try {
      await api.post("/emergency/beds", {
        hospital_id: bedH.id, bed_type: bedType,
        patient_name: pName, contact,
      });
      toast.success(`${bedType.toUpperCase()} bed reserved at ${bedH.name}`);
      setBedH(null); load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const bookAmb = async () => {
    if (!user) { nav("/login"); return; }
    if (!pickup || !contact) { toast.error("Fill pickup & contact"); return; }
    if (dropMode === "custom" && !dropAddress) { toast.error("Enter a drop-off address"); return; }
    if (dropMode === "hospital" && !destH) { toast.error("Select a destination hospital"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/emergency/ambulance", {
        ambulance_type: ambType, pickup_address: pickup,
        pickup_lat: loc?.lat, pickup_lng: loc?.lng,
        destination_hospital_id: dropMode === "hospital" ? (destH || null) : null,
        drop_address: dropMode === "custom" ? dropAddress : null,
        contact,
      });
      toast.success(`Ambulance ${data.vehicle_number} on the way — ETA ${data.eta_minutes} min · ₹${data.amount}`);
      setAmbOpen(false);
      nav(`/track/${data.id}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const estFare = () => {
    const base = { basic: 500, als: 1500, icu: 3000 }[ambType];
    const perKm = { basic: 25, als: 60, icu: 100 }[ambType];
    // Rough estimate for UI: assume 6 km if we don't know
    return Math.round(base + perKm * 6);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Emergency Care</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Bed reservation & ambulance dispatch</h1>
          <p className="text-slate-600 text-sm mt-1">Reserve a bed before the ambulance departs.</p>
        </div>
        <a href="tel:108" data-testid="page-sos" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold emergency-pulse">
          <PhoneCall className="w-4 h-4" /> Call 108
        </a>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to="/ai-triage"><Button variant="outline" data-testid="emergency-ai-cta"><Sparkles className="w-4 h-4 mr-2" />Ask MedHOne AI first</Button></Link>
        <LocationBar />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="emergency-tabs">
          <TabsTrigger value="bed" data-testid="tab-bed">Hospital & Bed</TabsTrigger>
          <TabsTrigger value="ambulance" data-testid="tab-ambulance">Ambulance</TabsTrigger>
        </TabsList>

        <TabsContent value="bed">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {hospitals.map((h) => {
              const totalBeds = Object.values(h.beds || {}).reduce((a, b) => a + b, 0);
              return (
                <Card key={h.id} className="p-5 rounded-2xl card-lift" data-testid={`hospital-card-${h.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-red-500/15 flex items-center justify-center text-red-700">
                      <Hospital className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <Link to={`/facility/hospital/${h.id}`} className="font-display font-bold hover:text-teal-700 transition-colors" data-testid={`hospital-detail-${h.id}`}>{h.name}</Link>
                      <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{h.address}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {["general", "icu", "nicu", "ventilator"].map((k) => (
                      <div key={k} className="text-center">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                        <div className="font-mono font-bold text-slate-900">{h.beds?.[k] || 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{totalBeds} beds</Badge>
                      {h.distance_km != null && <Badge variant="outline" className="text-[10px]" data-testid={`hosp-dist-${h.id}`}>{h.distance_km} km</Badge>}
                    </div>
                    <Button data-testid={`reserve-btn-${h.id}`} size="sm" className="bg-red-600 hover:bg-red-500 text-white" onClick={() => setBedH(h)}>Reserve</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="ambulance">
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {AMB_TYPES.map((t) => (
              <Card key={t.key} className="p-6 rounded-2xl card-lift" data-testid={`ambulance-type-${t.key}`}>
                <t.icon className="w-8 h-8 text-red-600 mb-3" />
                <div className="font-display text-xl font-bold">{t.label}</div>
                <div className="text-sm text-slate-600 mt-1">{t.desc}</div>
                <div className="mt-4 font-mono text-2xl font-bold">₹{t.price}</div>
                <Button data-testid={`book-amb-${t.key}`} className="mt-4 w-full bg-red-600 hover:bg-red-500 text-white" onClick={() => { setAmbType(t.key); setAmbOpen(true); }}>
                  Book {t.label}
                </Button>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Bed reservation dialog */}
      <Dialog open={!!bedH} onOpenChange={(o) => !o && setBedH(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reserve bed — {bedH?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Bed type</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["general", "icu", "nicu", "ventilator"].map((k) => (
                  <Badge
                    key={k}
                    data-testid={`bed-type-${k}`}
                    onClick={() => setBedType(k)}
                    className={`cursor-pointer capitalize ${bedType === k ? "bg-red-600" : "bg-slate-200 text-slate-800"}`}
                  >
                    {k} ({bedH?.beds?.[k] || 0})
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Patient name</Label>
              <Input data-testid="bed-patient-name" value={pName} onChange={(e) => setPName(e.target.value)} />
            </div>
            <div>
              <Label>Contact number</Label>
              <Input data-testid="bed-contact" value={contact} onChange={(e) => setContact(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="bed-confirm" disabled={loading} onClick={bookBed} className="bg-red-600 hover:bg-red-500 text-white">
              {loading ? "Reserving…" : "Reserve bed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ambulance dialog */}
      <Dialog open={ambOpen} onOpenChange={setAmbOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ambulance className="w-5 h-5 text-red-600" /> Book {ambType.toUpperCase()} ambulance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "basic", label: "Basic", price: 500 },
                { k: "als", label: "ALS", price: 1500 },
                { k: "icu", label: "ICU", price: 3000 },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`amb-type-${t.k}`}
                  onClick={() => setAmbType(t.k)}
                  className={`p-3 rounded-lg border-2 text-left transition ${ambType === t.k ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className="text-[10px] text-slate-500">base ₹{t.price}</div>
                </button>
              ))}
            </div>

            <div>
              <Label className="flex items-center justify-between">
                <span>Pickup</span>
                <button type="button" className="text-xs text-teal-700 font-semibold" data-testid="use-my-location" onClick={requestLoc}>
                  Use my current location
                </button>
              </Label>
              <Input data-testid="amb-pickup" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="e.g. 12 Boring Road, Patna" />
              {loc && <div className="text-[10px] text-emerald-700 mt-1">📍 GPS attached ({loc.lat.toFixed(3)}, {loc.lng.toFixed(3)})</div>}
            </div>

            <div>
              <Label>Drop-off</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <Badge
                  data-testid="drop-mode-hospital"
                  className={`cursor-pointer ${dropMode === "hospital" ? "bg-red-600" : "bg-slate-200 text-slate-800"}`}
                  onClick={() => setDropMode("hospital")}
                >
                  Nearby hospital
                </Badge>
                <Badge
                  data-testid="drop-mode-custom"
                  className={`cursor-pointer ${dropMode === "custom" ? "bg-red-600" : "bg-slate-200 text-slate-800"}`}
                  onClick={() => setDropMode("custom")}
                >
                  Custom address
                </Badge>
              </div>
              {dropMode === "hospital" ? (
                <select data-testid="amb-dest" className="w-full mt-1 border border-slate-200 rounded-md h-10 px-3 text-sm" value={destH} onChange={(e) => setDestH(e.target.value)}>
                  <option value="">— Choose hospital —</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>{h.name}{h.distance_km != null ? ` · ${h.distance_km} km` : ""}</option>
                  ))}
                </select>
              ) : (
                <Input data-testid="amb-drop-address" value={dropAddress} onChange={(e) => setDropAddress(e.target.value)} placeholder="e.g. Home address, care home, etc." />
              )}
            </div>

            <div>
              <Label>Contact number</Label>
              <Input data-testid="amb-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="+91…" />
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between text-sm">
              <div>
                <div className="text-xs text-slate-500">Estimated fare</div>
                <div className="font-mono font-bold text-lg">₹{estFare()}</div>
              </div>
              <div className="text-right text-[11px] text-slate-500">
                Base ₹{{basic:500,als:1500,icu:3000}[ambType]} + ₹{{basic:25,als:60,icu:100}[ambType]}/km · ETA ~10 min
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="amb-confirm" disabled={loading} onClick={bookAmb} className="bg-red-600 hover:bg-red-500 text-white">
              <Ambulance className="w-4 h-4 mr-2" />
              {loading ? "Dispatching…" : "Confirm & dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
